import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { BehaviorSubject, debounceTime, fromEvent, map, startWith } from 'rxjs';
import { View } from '../../core/interfaces';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class ScreenSizeService {
  private readonly DRAWER_SIZE = 400;
  // Define breakpoints or any criteria you want to use for screen sizes
  private readonly screenSizeSubject = new BehaviorSubject<number>(
    this.getScreenSize(),
  );
  private readonly contentSizeSubject = new BehaviorSubject<number>(
    this.getScreenSize() - this.DRAWER_SIZE,
  ); // 400 is accounted for the drawer being opened
  private drawerState: 'open' | 'close' = 'open';

  // Observable to expose screen and content size changes
  screenSize$ = this.screenSizeSubject.asObservable();
  contentSize$ = this.contentSizeSubject.asObservable();

  private readonly screenViewSubject = new BehaviorSubject<View>('web');

  screenView$ = this.screenViewSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      // Listen to window resize events
      fromEvent(window, 'resize')
        .pipe(
          debounceTime(200), // Prevents too many rapid resize events
          map(() => this.getScreenSize()),
          startWith(this.getScreenSize()), // Emit the initial size when service is initialized
        )
        .subscribe((size) => {
          this.screenSizeSubject.next(size);
          this.screenViewSubject.next(this.getScreenView(size));
          this.drawerState === 'open'
            ? this.contentSizeSubject.next(size - this.DRAWER_SIZE)
            : this.contentSizeSubject.next(size);
        });
    }
  }

  setContentSize(size: number) {
    this.contentSizeSubject.next(size);
  }

  setDrawerState(state: 'open' | 'close') {
    this.drawerState = state;
  }

  // Determine screen size based on window width
  private getScreenSize(): number {
    if (!isPlatformBrowser(this.platformId)) return 0;

    return window.innerWidth;
  }

  private getScreenView(width: number): View {
    if (!isPlatformBrowser(this.platformId)) return 'web';

    if (width < 1024) {
      return 'mobile';
    }

    return 'web';
  }
}
